import { useMatches } from "@tanstack/react-router";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CopilotPageContextProvider } from "@/features/copilot/components/copilot-page-context-provider";
import { CopilotPanel } from "@/features/copilot/components/copilot-panel";
import { HandMark } from "@/features/copilot/components/hand-mark";
import { useCopilotConversation } from "@/features/copilot/hooks/use-copilot-conversation";
import { usePanelMode } from "@/features/copilot/hooks/use-copilot-panel-mode";
import { copilotPageContextFromMatches } from "@/features/copilot/lib/copilot-page-context";
import { conversationForCopilotScope } from "@/features/copilot/lib/copilot-panel-scope";
import type { CopilotScopeKey } from "@/features/copilot/lib/copilot-query-keys";
import {
  isCopilotSettlementReady,
  observeCopilotServerTurn,
  type PendingCopilotSettlement,
  withoutApprovalSettlement,
} from "@/features/copilot/lib/copilot-turn-settlement";
import { useAuth } from "@/shared/auth/auth.context";
import { AppShell } from "@/shared/components/layout/app-shell";
import { useCopilotLayout } from "@/shared/components/layout/copilot-layout.context";
import { usePageBreadcrumbState } from "@/shared/components/layout/page-breadcrumb";
import { Button } from "@/shared/components/ui/button";
import { Sheet } from "@/shared/components/ui/sheet";
import { useCredits } from "@/shared/entitlements/hooks/use-credits";
import { useActiveWorkspace } from "@/shared/hooks/use-active-workspace";

/** Inside the shell, so the record name a page registers for its breadcrumb is in reach. */
function CopilotRoutePageContext({ children }: { children: ReactNode }) {
  const { subject } = usePageBreadcrumbState();
  const context = copilotPageContextFromMatches(useMatches(), subject);
  return <CopilotPageContextProvider context={context}>{children}</CopilotPageContextProvider>;
}

export function CopilotAppComposition({ children }: { children: ReactNode }) {
  const layout = useCopilotLayout();
  const auth = useAuth();
  const { workspace, isResolved, isSwitching } = useActiveWorkspace();
  const credits = useCredits();
  const mode = usePanelMode();
  const [restoreRevision, setRestoreRevision] = useState(0);
  const [restoreFailureScope, setRestoreFailureScope] = useState<string | null>(null);
  const [createFailureScope, setCreateFailureScope] = useState<string | null>(null);
  const [runtimeActivity, setRuntimeActivity] = useState<{
    scopeKey: string | null;
    conversationId: string | null;
    conversationKey: string | null;
    running: boolean;
  }>({ scopeKey: null, conversationId: null, conversationKey: null, running: false });
  const pendingSettlement = useRef<PendingCopilotSettlement | null>(null);
  const lastSettledTurn = useRef<{
    scopeKey: string;
    conversationId: string;
    turnId: string | null;
  } | null>(null);

  const scope = useMemo<CopilotScopeKey | null>(() => {
    const workspaceIsOwned = auth.workspaces.some((entry) => entry.id === workspace.id);
    if (
      !auth.isAuthenticated ||
      !auth.account?.id ||
      !auth.user?.id ||
      !workspaceIsOwned ||
      !isResolved ||
      isSwitching
    ) {
      return null;
    }
    return { accountId: auth.account.id, userId: auth.user.id, workspaceId: workspace.id };
  }, [
    auth.account?.id,
    auth.isAuthenticated,
    auth.user?.id,
    auth.workspaces,
    isResolved,
    isSwitching,
    workspace.id,
  ]);
  const scopeKey = scope
    ? JSON.stringify([scope.accountId, scope.userId, scope.workspaceId])
    : null;
  const previousScopeKey = useRef(scopeKey);
  const localRuntimeConversationId =
    scopeKey !== null && runtimeActivity.scopeKey === scopeKey && runtimeActivity.running
      ? runtimeActivity.conversationId
      : null;
  const { current, create } = useCopilotConversation(scope, localRuntimeConversationId);
  const refetchCurrent = current.refetch;
  const conversation = conversationForCopilotScope(current.data, scope);
  const conversationKey =
    scopeKey && conversation ? JSON.stringify([scopeKey, conversation.id, restoreRevision]) : null;
  const activeTurn = conversation?.activeTurn;
  const conversationId = conversation?.id;
  const currentTurnId = current.data?.activeTurn?.id;
  if (scopeKey && conversationId && activeTurn?.status !== "running") {
    lastSettledTurn.current = { scopeKey, conversationId, turnId: activeTurn?.id ?? null };
  }
  const restoreError = scopeKey !== null && restoreFailureScope === scopeKey;
  const createError = scopeKey !== null && createFailureScope === scopeKey;

  const createForCurrentScope = useCallback(() => {
    if (!scope || !scopeKey) return;
    setCreateFailureScope(null);
    create.mutate(scope, {
      onError: () => setCreateFailureScope(scopeKey),
    });
  }, [create.mutate, scope, scopeKey]);

  const ensureConversation = useCallback(() => {
    if (!scope) return;
    if (current.isSuccess) {
      if (current.data === null) createForCurrentScope();
      return;
    }
    void current.refetch().then((result) => {
      if (result.isSuccess && result.data === null) createForCurrentScope();
    });
  }, [current, createForCurrentScope, scope]);

  useEffect(() => {
    if (previousScopeKey.current === scopeKey) return;
    previousScopeKey.current = scopeKey;
    if (!layout.isOpen || !scope || !scopeKey) return;

    let isCurrentScope = true;
    void current.refetch().then((result) => {
      if (!isCurrentScope || !result.isSuccess || result.data !== null) return;
      createForCurrentScope();
    });
    return () => {
      isCurrentScope = false;
    };
  }, [createForCurrentScope, current.refetch, layout.isOpen, scope, scopeKey]);

  const open = useCallback(() => {
    layout.open();
    ensureConversation();
  }, [ensureConversation, layout]);

  const restore = useCallback(async () => {
    setRestoreFailureScope(null);
    const result = await refetchCurrent();
    if (!result.isSuccess || !result.data) {
      setRestoreFailureScope(scopeKey);
      throw new Error("The latest Copilot conversation could not be restored.");
    }
    pendingSettlement.current = null;
    setRestoreRevision((revision) => revision + 1);
  }, [refetchCurrent, scopeKey]);

  const updateRuntimeActivity = useCallback(
    (running: boolean) => {
      setRuntimeActivity({
        scopeKey,
        conversationId: conversationId ?? null,
        conversationKey,
        running,
      });
      if (running && scopeKey && conversationId) {
        const pending = pendingSettlement.current;
        const approvalSettlementIsActive =
          pending?.kind === "approval" &&
          pending.scopeKey === scopeKey &&
          pending.conversationId === conversationId;
        if (approvalSettlementIsActive) {
          void refetchCurrent();
          return;
        }
        const lastTurn = lastSettledTurn.current;
        pendingSettlement.current = {
          scopeKey,
          conversationId,
          previousTurnId:
            lastTurn?.scopeKey === scopeKey && lastTurn.conversationId === conversationId
              ? lastTurn.turnId
              : activeTurn?.status === "running"
                ? null
                : (currentTurnId ?? null),
          turnId: null,
        };
      }
      void refetchCurrent();
    },
    [activeTurn?.status, conversationId, conversationKey, currentTurnId, refetchCurrent, scopeKey],
  );

  const startApprovalSettlement = useCallback(
    (turnId: string, settlementRevision: number) => {
      if (!scopeKey || !conversationId) return;
      pendingSettlement.current = {
        scopeKey,
        conversationId,
        previousTurnId: turnId,
        turnId,
        kind: "approval",
        previousSettlementRevision: settlementRevision,
      };
      void refetchCurrent();
    },
    [conversationId, refetchCurrent, scopeKey],
  );

  const clearApprovalSettlement = useCallback(() => {
    pendingSettlement.current = withoutApprovalSettlement(pendingSettlement.current);
  }, []);

  const refreshAfterStop = useCallback(async () => {
    const pendingAtStop = pendingSettlement.current;
    const result = await refetchCurrent();
    if (!result.isSuccess || !result.data) return;
    const refreshedTurn = result.data.activeTurn;
    const stillSameSettlement =
      scopeKey !== null &&
      result.data.id === conversationId &&
      pendingAtStop !== null &&
      pendingAtStop.scopeKey === scopeKey &&
      pendingAtStop.conversationId === result.data.id &&
      pendingSettlement.current === pendingAtStop;
    if (stillSameSettlement && refreshedTurn) {
      pendingSettlement.current = observeCopilotServerTurn(pendingAtStop, refreshedTurn.id);
      setRuntimeActivity((activity) => ({ ...activity }));
      return;
    }
    if (!pendingAtStop) setRestoreRevision((revision) => revision + 1);
  }, [conversationId, refetchCurrent, scopeKey]);

  useEffect(() => {
    if (!scopeKey || !conversationId) {
      pendingSettlement.current = null;
      return;
    }
    const pending = pendingSettlement.current;
    if (pending && (pending.scopeKey !== scopeKey || pending.conversationId !== conversationId)) {
      pendingSettlement.current = null;
      return;
    }
    if (activeTurn?.status === "running") {
      pendingSettlement.current = pending
        ? observeCopilotServerTurn(pending, activeTurn.id)
        : {
            scopeKey,
            conversationId,
            previousTurnId: null,
            turnId: activeTurn.id,
          };
      return;
    }
    const observedPending = pending
      ? observeCopilotServerTurn(pending, activeTurn?.id ?? null)
      : null;
    if (pending && observedPending !== pending) pendingSettlement.current = observedPending;
    const localRuntimeRunning =
      conversationKey !== null &&
      runtimeActivity.conversationKey === conversationKey &&
      runtimeActivity.running;
    if (
      !isCopilotSettlementReady({
        pending: observedPending,
        scopeKey,
        conversationId,
        serverTurnId: activeTurn?.id ?? null,
        turnStatus: activeTurn?.status ?? null,
        settlementRevision: activeTurn?.settlementRevision,
        localRuntimeRunning,
      })
    ) {
      return;
    }
    pendingSettlement.current = null;
    setRestoreRevision((revision) => revision + 1);
  }, [
    activeTurn?.id,
    activeTurn?.settlementRevision,
    activeTurn?.status,
    conversationId,
    conversationKey,
    runtimeActivity,
    scopeKey,
  ]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && layout.isOpen) {
        // Escape aimed at a page popover or select must not also close Hand.
        const target = event.target;
        const inHand = target instanceof Element && target.closest("#copilot-panel") !== null;
        if (!inHand && target !== document.body) return;
        event.preventDefault();
        // Step down one level at a time: full page to dock, dock to closed.
        if (layout.isExpanded) {
          layout.toggleExpanded();
          return;
        }
        layout.close();
        window.requestAnimationFrame(() => layout.triggerRef.current?.focus());
        return;
      }
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.key.toLowerCase() !== "j")
        return;
      event.preventDefault();
      if (layout.isOpen) {
        layout.close();
        window.requestAnimationFrame(() => layout.triggerRef.current?.focus());
      } else open();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [layout, open]);

  const trigger = (
    <Button
      ref={layout.triggerRef}
      type="button"
      variant="outline"
      size="sm"
      onClick={() => (layout.isOpen ? layout.close() : open())}
      aria-expanded={layout.isOpen}
      aria-controls="copilot-panel"
      aria-label="Toggle Hand (Ctrl or Command J)"
      title="Hand (Ctrl or Command J)"
    >
      <HandMark />
      <span className="hidden xl:inline">Hand</span>
    </Button>
  );

  return (
    <Sheet open={layout.isOpen} onOpenChange={(openState) => (openState ? open() : layout.close())}>
      <AppShell
        copilotTrigger={trigger}
        copilotPanel={
          <CopilotRoutePageContext>
            <CopilotPanel
              mode={mode}
              scope={scope}
              conversation={conversation}
              restoreRevision={restoreRevision}
              onRestoreRequired={restore}
              onApprovalStarted={startApprovalSettlement}
              onApprovalFailed={clearApprovalSettlement}
              onNewConversation={createForCurrentScope}
              onRuntimeRunningChange={updateRuntimeActivity}
              onAfterStop={refreshAfterStop}
              loading={scope !== null && current.isLoading}
              loadError={scope !== null && current.isError && !conversation}
              createError={createError}
              scopeUnavailable={scope === null}
              restoreError={restoreError}
              noCredits={credits.remaining === 0}
            />
          </CopilotRoutePageContext>
        }
      >
        {children}
      </AppShell>
    </Sheet>
  );
}
